import React from 'react';
import FCategoryIcon, { resolveFCategory } from '../fCategoryIcons';
import './V2GoalRow.css';

/**
 * Compact dashboard goal row — Step 2 GoalCardV2 visual language, list-sized.
 * Marker shows the 7 F category icon; goal number sits beside it.
 * V2 only; does not affect classic SelectedGoal.
 */
export default function V2GoalRow({
    goalNumber,
    title,
    category,
    targetDate,
    status = 'In Progress',
    isCompleted = false,
    isOolaOne = false,
    accentColor = '#c9a227',
    menuOpen = false,
    menuOpensUp = false,
    menuRef,
    showMenu = true,
    showAddToTop7 = false,
    /** When true, click/Enter/Space on the row opens complete (⋯ menu still works). */
    rowClickable = false,
    onMenuToggle,
    onAddToTop7,
    onComplete,
    onEdit,
    onRemove,
}) {
    const numLabel =
        goalNumber != null && goalNumber !== '' ? String(goalNumber) : '·';
    const fName = resolveFCategory(category);
    const categoryLabel = fName || category;
    const canActivate = Boolean(rowClickable && onComplete);

    const activateComplete = () => {
        if (canActivate) onComplete();
    };

    return (
        <li
            className={`dv2-goal-row${isCompleted ? ' is-done' : ''}${
                isOolaOne ? ' is-oola-one' : ''
            }${menuOpen ? ' is-menu-open' : ''}${
                canActivate ? ' is-clickable' : ''
            }`}
            style={{ ['--dv2-goal-accent']: accentColor }}
            onClick={canActivate ? activateComplete : undefined}
            onKeyDown={
                canActivate
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              activateComplete();
                          }
                      }
                    : undefined
            }
            role={canActivate ? 'button' : undefined}
            tabIndex={canActivate ? 0 : undefined}
            aria-label={
                canActivate
                    ? `Complete: ${title || 'goal'}`
                    : undefined
            }
        >
            <div className="dv2-goal-row__marker">
                <span
                    className={`dv2-goal-row__badge${
                        isCompleted ? ' is-done' : ''
                    }`}
                    aria-hidden="true"
                >
                    <FCategoryIcon category={category} size={15} />
                    {isCompleted ? (
                        <span className="dv2-goal-row__badge-check">
                            <svg
                                width="9"
                                height="9"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <polyline
                                    points="20 6 9 17 4 12"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    ) : null}
                </span>
                <span className="dv2-goal-row__num" aria-hidden="true">
                    {numLabel}
                </span>
            </div>

            <div className="dv2-goal-row__body">
                <div className="dv2-goal-row__title">{title || 'Goal'}</div>
                <div className="dv2-goal-row__meta">
                    {isOolaOne ? (
                        <>
                            <span className="dv2-goal-row__oola">OOLA ONE</span>
                            {categoryLabel || targetDate ? (
                                <span
                                    className="dv2-goal-row__sep"
                                    aria-hidden="true"
                                >
                                    ·
                                </span>
                            ) : null}
                        </>
                    ) : null}
                    {categoryLabel ? (
                        <span className="dv2-goal-row__category">
                            <FCategoryIcon
                                category={category}
                                size={12}
                                className="dv2-goal-row__category-icon"
                            />
                            {categoryLabel}
                        </span>
                    ) : null}
                    {categoryLabel && targetDate ? (
                        <span className="dv2-goal-row__sep" aria-hidden="true">
                            ·
                        </span>
                    ) : null}
                    {targetDate ? (
                        <span className="dv2-goal-row__date">{targetDate}</span>
                    ) : null}
                </div>
            </div>

            <div
                className="dv2-goal-row__trail"
                ref={menuOpen ? menuRef : null}
            >
                <span
                    className={`dv2-goal-row__status${
                        isCompleted ? ' is-done' : ''
                    }${status === 'Pending' ? ' is-pending' : ''}`}
                >
                    {!isCompleted ? (
                        <span
                            className={`dv2-goal-row__status-dot${
                                status === 'Pending' ? ' is-pending' : ''
                            }`}
                            aria-hidden
                        />
                    ) : null}
                    {status}
                </span>

                {showMenu ? (
                    <div
                        className="dv2-goal-row__menu-wrap"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="dv2-goal-row__menu-btn"
                            aria-label={`Actions for ${title || 'goal'}`}
                            aria-expanded={menuOpen}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (typeof onMenuToggle === 'function') {
                                    onMenuToggle();
                                }
                            }}
                        >
                            ⋯
                        </button>
                        {menuOpen ? (
                            <div
                                className={`dv2-goal-row__menu${
                                    menuOpensUp ? ' is-up' : ''
                                }`}
                                role="menu"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {showAddToTop7 ? (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (typeof onAddToTop7 === 'function') {
                                                onAddToTop7();
                                            }
                                        }}
                                    >
                                        Add to My Top 7
                                    </button>
                                ) : null}
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (typeof onComplete === 'function') {
                                            onComplete();
                                        }
                                    }}
                                >
                                    Complete
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (typeof onEdit === 'function') {
                                            onEdit();
                                        }
                                    }}
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="dv2-goal-row__menu-danger"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (typeof onRemove === 'function') {
                                            onRemove();
                                        }
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </li>
    );
}
